/**
 * Notify-me — global delegation for all variant selectors.
 *
 * Any `[data-action="notify-me"]` element on the page opens
 * `#condition-notify-modal`, prefilled with the variant label
 * carried in `data-condition`, plus the out-of-stock target's
 * URL + title in `data-notify-url` / `data-notify-title` — folded
 * into the contact body so the owner sees the exact OOS variant
 * (Shopify /contact drops custom params, body always survives). Used by:
 *   - Condition selector (OOS grade rows)
 *   - Color selector   (OOS color swatches)
 *   - Storage selector (OOS memorie cards)
 *
 * The custom element `<cos-condition-selector>` stays as a structural
 * wrapper; click handling no longer lives on it (kept for any external
 * code that queries the tag).
 */

(function () {
  const TAG = 'cos-condition-selector';
  if (!customElements.get(TAG)) {
    customElements.define(TAG, class extends HTMLElement {});
  }

  /* ---------------- Global click delegation ---------------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action="notify-me"]');
    if (!trigger) return;
    event.preventDefault();
    const condition = trigger.getAttribute('data-condition') || '';
    const targetUrl = trigger.getAttribute('data-notify-url') || '';
    const targetTitle = trigger.getAttribute('data-notify-title') || '';
    const modal = document.getElementById('condition-notify-modal');
    if (!modal) return;
    // Exact OOS product/variant for STOQ; reset per click so no stale ids leak.
    modal.dataset.stoqProductId = trigger.getAttribute('data-notify-product-id') || '';
    modal.dataset.stoqVariantId = trigger.getAttribute('data-notify-variant-id') || '';
    prefillNotifyModal(modal, condition, targetUrl, targetTitle);
    modal.showModal();
  });

  /* ---------------- notify modal helpers ---------------- */

  function prefillNotifyModal(modal, condition, targetUrl, targetTitle) {
    const label = modal.querySelector('[data-notify-condition]');
    const conditionInput = modal.querySelector('[data-notify-condition-input]');
    const bodyInput = modal.querySelector('[data-notify-body]');
    const productUrl = modal.querySelector('input[name="contact[product_url]"]')?.value || window.location.href;
    const productTitle = modal.getAttribute('data-notify-product-title') || '';
    const display = condition === 'any' ? 'oricare variantă' : (condition || productTitle || '—');
    if (label) label.textContent = display;
    if (conditionInput) conditionInput.value = condition;
    if (bodyInput) {
      // Fold every identifier into the body — /contact keeps body verbatim,
      // but silently drops custom contact[...] params. targetUrl is the exact
      // OOS sibling product (split-variant architecture); productUrl is the
      // page the client was viewing when they asked to be notified.
      const lines = [`Client vrea să fie notificat când ${display} revine în stoc.`];
      if (targetUrl) {
        lines.push(`Variantă dorită: ${targetTitle || display} — ${targetUrl}`);
      }
      lines.push(`Produs vizualizat: ${productUrl}`);
      bodyInput.value = lines.join('\n');
    }

    // Reset modal to form view (in case user reopens after success)
    const form = modal.querySelector('[data-notify-form]');
    const success = modal.querySelector('[data-notify-success]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
  }

  // On load: if we just came back from a successful /contact POST, reopen
  // the notify modal in success mode so the user sees confirmation.
  document.addEventListener('DOMContentLoaded', () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('contact_posted') !== 'true') return;
    if (window.location.hash !== '#condition-notify-modal') return;
    const modal = document.getElementById('condition-notify-modal');
    if (!modal) return;

    const form = modal.querySelector('[data-notify-form]');
    const success = modal.querySelector('[data-notify-success]');
    const condition = sessionStorage.getItem('cond-notify-last') || '';
    sessionStorage.removeItem('cond-notify-last');

    if (form) form.hidden = true;
    if (success) {
      success.hidden = false;
      const condLabel = success.querySelector('[data-notify-success-condition]');
      if (condLabel) condLabel.textContent = condition || '—';
    }

    modal.showModal();
  });

  // Persist the chosen condition so we can restore it after redirect.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-notify-form]');
    if (!form) return;
    const cond = form.querySelector('[data-notify-condition-input]')?.value || '';
    sessionStorage.setItem('cond-notify-last', cond);

    // Fold the phone into the body. /contact keeps the body verbatim but drops
    // custom contact[...] params, so the owner email may never surface the phone
    // field on its own — the body is the only reliable channel. Strip any prior
    // phone line first so this stays idempotent across re-submits.
    const phone = (form.querySelector('input[name="contact[phone]"]')?.value || '').trim();
    const bodyInput = form.querySelector('[data-notify-body]');
    if (bodyInput) {
      const base = bodyInput.value
        .split('\n')
        .filter((line) => !line.startsWith('Telefon:'))
        .join('\n');
      bodyInput.value = phone ? `${base}\nTelefon: ${phone}` : base;
    }

    // STOQ restock intent. The /contact POST navigates away and would cancel
    // an in-flight fetch, so hold the native submit until STOQ answers (max
    // 3s), then submit. A STOQ failure never blocks the /contact request.
    const modal = form.closest('#condition-notify-modal');
    const productId = modal?.dataset.stoqProductId;
    const variantId = modal?.dataset.stoqVariantId;
    const email = (form.querySelector('input[name="contact[email]"]')?.value || '').trim();
    if (!productId || !variantId || !email) return;
    e.preventDefault();
    e.submitter?.setAttribute('disabled', '');
    fetch('https://app.stoqapp.com/api/v1/intents.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Shop-Domain': modal.dataset.stoqShop,
      },
      body: JSON.stringify({
        intent: {
          shopify_product_id: Number(productId),
          shopify_variant_id: Number(variantId),
          channel: 'email',
          quantity: 1,
          source: 'api',
        },
        customer: {
          email,
          accepts_marketing: false,
          locale: modal.dataset.stoqLocale || 'ro',
        },
      }),
      signal: AbortSignal.timeout?.(3000),
    })
      .catch(() => {})
      .finally(() => form.submit());
  });
})();
